import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  IconButton,
  InputAdornment,
  Button,
  Typography,
  Alert,
  Link,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import AuthDarkTextField from './AuthDarkTextField';
import AuthMobileCard from './AuthMobileCard';
import { darkPrimaryButtonSx, darkLinkSx, darkAlertSx } from './loginDarkTheme';
import { getNativePlatform, isNativeAppShell } from '../../utils/mobileView';

export default function LoginCredentialsStep({
  email,
  password,
  showPassword,
  rememberMe = false,
  errors,
  isSubmitting,
  emailInputRef,
  passwordInputRef,
  onEmailChange,
  onPasswordChange,
  onTogglePassword,
  onRememberMeChange,
  onSubmit,
  onOpenTerms,
  onOpenFreeTrial,
  submitLabel = 'Login',
  submittingLabel = 'Signing in...',
  variant = 'desktop',
}) {
  const navigate = useNavigate();
  const isDarkMobile = variant === 'mobile-dark';
  const hideFreeTrialLink = getNativePlatform() === 'ios';
  const isNative = isNativeAppShell();
  const rememberLockedOnNative = isNative;

  const desktopFieldSx = {
    mb: 3,
    '& .MuiOutlinedInput-root': {
      borderRadius: '0',
      backgroundColor: '#fafafa',
      fontSize: '16px',
      height: '56px',
      color: '#212121',
      '&:hover fieldset': { borderColor: '#4ce1b6', borderWidth: '1px' },
      '&.Mui-focused fieldset': { borderColor: '#4ce1b6', borderWidth: '1px' },
      '& fieldset': { borderColor: '#e0e0e0', borderWidth: '1px' },
    },
    '& .MuiInputLabel-root': {
      fontSize: '15px',
      color: '#757575',
      '&.Mui-focused': { color: '#4ce1b6', fontWeight: 500 },
    },
    '& .MuiInputBase-input': { fontSize: '16px', color: '#212121', padding: '16.5px 16px' },
    '& .MuiFormHelperText-root': { fontSize: '12px', marginLeft: '4px', marginTop: '6px' },
  };

  const primaryButtonSx = isDarkMobile
    ? { ...darkPrimaryButtonSx, mb: 0 }
    : {
        py: 1.75,
        mb: 0,
        backgroundColor: '#000000',
        color: '#ffffff',
        borderRadius: '0',
        textTransform: 'none',
        fontSize: '16px',
        fontWeight: 600,
        height: '52px',
        '&:disabled': { backgroundColor: '#9e9e9e', color: '#ffffff' },
      };

  const passwordToggle = (
    <InputAdornment position="end">
      <IconButton
        aria-label="toggle password visibility"
        onClick={onTogglePassword}
        edge="end"
        sx={{
          color: isDarkMobile ? 'rgba(148, 163, 184, 0.9)' : '#757575',
          '&:hover': { color: '#4ce1b6', backgroundColor: 'rgba(76, 225, 182, 0.08)' },
        }}
      >
        {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
      </IconButton>
    </InputAdornment>
  );

  const formFields = isDarkMobile ? (
    <>
      <AuthDarkTextField
        type="text"
        label="Email or Username"
        name="email"
        autoComplete="username"
        inputRef={emailInputRef}
        value={email}
        onChange={onEmailChange}
        autoFocus
        error={!!errors.email}
        helperText={
          errors.email ? (Array.isArray(errors.email) ? errors.email.join(', ') : errors.email) : ''
        }
      />
      <AuthDarkTextField
        type={showPassword ? 'text' : 'password'}
        label="Password"
        name="password"
        autoComplete="current-password"
        inputRef={passwordInputRef}
        value={password}
        onChange={onPasswordChange}
        error={!!errors.password}
        helperText={
          errors.password
            ? Array.isArray(errors.password)
              ? errors.password.join(', ')
              : errors.password
            : ''
        }
        InputProps={{ endAdornment: passwordToggle }}
      />
    </>
  ) : (
    <>
      <AuthDarkTextField
        type="text"
        label="Email or Username"
        name="email"
        autoComplete="username"
        inputRef={emailInputRef}
        value={email}
        onChange={onEmailChange}
        autoFocus
        error={!!errors.email}
        helperText={
          errors.email ? (Array.isArray(errors.email) ? errors.email.join(', ') : errors.email) : ''
        }
        sx={desktopFieldSx}
      />
      <AuthDarkTextField
        type={showPassword ? 'text' : 'password'}
        label="Password"
        name="password"
        autoComplete="current-password"
        inputRef={passwordInputRef}
        value={password}
        onChange={onPasswordChange}
        error={!!errors.password}
        helperText={
          errors.password
            ? Array.isArray(errors.password)
              ? errors.password.join(', ')
              : errors.password
            : ''
        }
        InputProps={{ endAdornment: passwordToggle }}
        sx={desktopFieldSx}
      />
    </>
  );

  const content = (
    <>
      {errors.non_field_errors && (
        <Alert severity="error" sx={isDarkMobile ? darkAlertSx : { mb: 3, borderRadius: '12px' }}>
          {Array.isArray(errors.non_field_errors)
            ? errors.non_field_errors.map((error, index) => <div key={index}>{error}</div>)
            : errors.non_field_errors}
        </Alert>
      )}

      <Box component="form" onSubmit={onSubmit} noValidate sx={{ width: '100%' }}>
        {formFields}

        <Box
          sx={{
            mt: isDarkMobile ? -1 : -2,
            mb: isDarkMobile ? 1.75 : 2,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            columnGap: 1.25,
            rowGap: 0.75,
            width: '100%',
          }}
        >
          <FormControlLabel
            sx={{
              alignItems: 'center',
              mx: 0,
              flex: '1 1 auto',
              minWidth: 0,
              maxWidth: '100%',
              '& .MuiFormControlLabel-label': {
                color: isDarkMobile ? 'rgba(226, 232, 240, 0.92)' : '#374151',
                fontSize: { xs: '12.5px', sm: '13.5px' },
                lineHeight: 1.3,
                fontWeight: 600,
              },
            }}
            control={
              <Checkbox
                checked={Boolean(rememberMe) || rememberLockedOnNative}
                onChange={(e) => {
                  if (rememberLockedOnNative) return;
                  onRememberMeChange?.(e.target.checked);
                }}
                disabled={isSubmitting || rememberLockedOnNative}
                size="small"
                sx={{
                  color: isDarkMobile ? 'rgba(148, 163, 184, 0.9)' : '#9ca3af',
                  '&.Mui-checked': { color: '#4ce1b6' },
                  py: 0.25,
                  pl: 0,
                }}
              />
            }
            label="Keep me signed in"
          />
          <Link
            href="/password-reset/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/password-reset/');
            }}
            sx={
              isDarkMobile
                ? {
                    ...darkLinkSx,
                    flex: '0 0 auto',
                    ml: 'auto',
                    whiteSpace: 'nowrap',
                    fontSize: { xs: '12.5px', sm: '14px' },
                  }
                : {
                    color: '#4ce1b6',
                    textDecoration: 'none',
                    fontSize: { xs: '12.5px', sm: '14px' },
                    fontWeight: 500,
                    flex: '0 0 auto',
                    ml: 'auto',
                    whiteSpace: 'nowrap',
                    '&:hover': { textDecoration: 'underline', color: '#3dd1a3' },
                  }
            }
          >
            Forgot password?
          </Link>
        </Box>

        <Box sx={{ mb: isDarkMobile ? 2.5 : 3 }}>
          <Typography
            component="span"
            className={isDarkMobile ? undefined : 'login-agreement-text'}
            sx={{
              display: 'block',
              color: isDarkMobile ? 'rgba(226, 232, 240, 0.94)' : '#111827',
              fontSize: '14px',
              lineHeight: 1.6,
              textAlign: 'center',
              fontWeight: 700,
            }}
          >
            By continuing, you agree to RoomNexa&apos;s{' '}
            <Link
              href="/terms-and-conditions"
              onClick={(e) => {
                e.preventDefault();
                onOpenTerms?.();
              }}
              sx={isDarkMobile ? { ...darkLinkSx, fontWeight: 800 } : { color: '#16a085', fontWeight: 800 }}
            >
              Terms of Service
            </Link>
          </Typography>
        </Box>

        <Button type="submit" fullWidth variant="contained" disabled={isSubmitting} sx={primaryButtonSx}>
          {isSubmitting ? submittingLabel : submitLabel}
        </Button>

        {!hideFreeTrialLink ? (
          <Box sx={{ mt: isDarkMobile ? 1.5 : 1.25, textAlign: 'right' }}>
            <Link
              href="/free-trial/"
              onClick={(e) => {
                e.preventDefault();
                onOpenFreeTrial?.();
              }}
              sx={
                isDarkMobile
                  ? darkLinkSx
                  : {
                      color: '#4ce1b6',
                      textDecoration: 'none',
                      fontSize: '14px',
                      fontWeight: 500,
                      '&:hover': { textDecoration: 'underline', color: '#3dd1a3' },
                    }
              }
            >
              Start Free Trial
            </Link>
          </Box>
        ) : null}
      </Box>
    </>
  );

  if (isDarkMobile) {
    return <AuthMobileCard>{content}</AuthMobileCard>;
  }

  return content;
}
